/**
 * Resilient upload helpers for the timesheet scanner.
 *
 * Wraps a single-file upload with:
 *  - Idempotency: one client-generated UUID per file survives every
 *    retry so the backend never double-processes.
 *  - Exponential backoff on transient 5xx (Cloudflare 502/503/520/524,
 *    ingress restarts, pod cycling): retries at 1 s / 3 s / 7 s.
 *  - Pre-flight readiness probe (/api/health/upload-ready): rejects
 *    the upload before it even starts if the pod is under memory
 *    pressure. Better to warn the user up front than to eat a big
 *    upload transfer and then 502.
 *  - Correlation IDs: every attempt carries X-Request-ID so the
 *    ops team can grep a single ID through browser → CF → ingress →
 *    backend → OCR worker logs.
 *  - Friendly progress callbacks. The caller passes ``onProgress``
 *    and gets a stream of human-readable strings the review UI
 *    surfaces via toast/status line — no more "Upload failed" with
 *    zero context.
 *
 * What we DON'T do here:
 *  - Change OCR logic, extraction prompts, or business rules.
 *    This module is a pure transport-resiliency layer.
 *  - Retry on 4xx. Client errors (413 too large, 415 unsupported)
 *    are permanent and re-tossing the same body would just spin.
 */
import { logger } from './logger';
import api from '../services/api';

// UUID v4 generator using crypto.randomUUID with a graceful fallback
// for the (very rare) legacy browsers that don't expose it. We
// never send this key to the LLM — it's opaque transport metadata.
const newUuid = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID().replace(/-/g, '');
    }
  } catch {
    // fall through
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 14)}`;
};

// Backoff schedule for transient failures. Deliberately geometric
// (1, 3, 7 s = ~11 s total) rather than fixed — three retries buys us
// through one CF/pod-restart window without frustrating the user with
// a minute-long spinner.
const RETRY_DELAYS_MS = [1000, 3000, 7000];

// Any 5xx response OR a network-level error (no HTTP status) is
// treated as transient and retryable. Everything else (400, 401,
// 403, 413, 415, 422, ...) is permanent client-side and the user
// needs to correct the input.
const isRetryable = (err) => {
  const status = err?.response?.status;
  if (status == null) {
    // No HTTP response — network layer failure, CF connection reset,
    // browser fetch abort mid-flight. Retryable.
    return true;
  }
  return status >= 500 && status < 600;
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Pre-upload readiness probe. Returns:
 *   { ok: true }                     — safe to upload
 *   { ok: true, degraded: true, ... } — upload allowed, warn user
 *   { ok: false, message: string }   — refuse the upload
 *
 * Never throws — a broken probe endpoint should NOT block uploads
 * (falls open, like most resiliency probes).
 */
export const checkUploadReadiness = async () => {
  try {
    const res = await api.get('/health/upload-ready', { timeout: 5000 });
    const status = res.data?.status;
    if (status === 'reject') {
      return {
        ok: false,
        message:
          'System is temporarily under heavy load. Please try again in a minute.',
      };
    }
    if (status === 'degraded') {
      return {
        ok: true,
        degraded: true,
        message: 'Server is busy — this upload may take a little longer than usual.',
      };
    }
    return { ok: true };
  } catch (err) {
    // Probe failed. Don't block — the actual upload will surface any
    // real problem with its own retry logic.
    logger.warn?.('[upload] readiness probe failed, proceeding anyway', err);
    return { ok: true };
  }
};

/**
 * Upload one file with automatic retry + idempotency.
 *
 * @param {File} file — the file to upload.
 * @param {Object} opts
 * @param {(msg: string) => void} [opts.onProgress] — friendly status
 *   updates the caller can surface to the user.
 * @returns The parsed response body from the backend on success.
 * @throws  The last axios error if all attempts fail.
 */
export const uploadWithResilience = async (file, { onProgress } = {}) => {
  // Same UUID for every attempt of THIS file — the backend uses it to
  // dedupe retries (see backend/services/upload_idempotency.py).
  const idempotencyKey = newUuid();
  // Fresh request ID per attempt so ops can distinguish retries in
  // the audit log; the idempotency key is what stitches them together.
  let attempt = 0;
  let lastError;

  while (attempt <= RETRY_DELAYS_MS.length) {
    const requestId = newUuid();
    const formData = new FormData();
    formData.append('file', file);
    const attemptLabel = attempt === 0 ? '' : ` (retry ${attempt}/${RETRY_DELAYS_MS.length})`;
    onProgress?.(`Uploading ${file.name}${attemptLabel}…`);

    try {
      const res = await api.post('/timesheets/upload', formData, {
        timeout: 300000,
        headers: {
          'X-Upload-Idempotency-Key': idempotencyKey,
          'X-Request-ID': requestId,
        },
      });
      if (attempt > 0) {
        // Confirm to the user their upload succeeded on retry — much
        // better UX than silently swallowing the earlier 5xx.
        onProgress?.(`${file.name} — recovered after ${attempt} retry(s).`);
      }
      return res.data;
    } catch (err) {
      lastError = err;
      if (!isRetryable(err) || attempt >= RETRY_DELAYS_MS.length) {
        throw err;
      }
      const wait = RETRY_DELAYS_MS[attempt];
      const reason = err?.response?.status ?? 'network';
      onProgress?.(
        `Connection interrupted (${reason}). Retrying "${file.name}" in ${Math.round(
          wait / 1000
        )}s…`
      );
      logger.warn?.(
        `[upload-retry] file=${file.name} attempt=${attempt + 1} reason=${reason} idem=${idempotencyKey.slice(0, 8)}`
      );
      await sleep(wait);
      attempt += 1;
    }
  }
  throw lastError;
};

/**
 * Human-readable failure message for the final error after all retries.
 * Prefers the backend's ``detail`` field when present.
 */
export const humanizeUploadError = (err, filename) => {
  const detail = err?.response?.data?.detail;
  if (detail) return `${filename}: ${detail}`;
  const status = err?.response?.status;
  if (status >= 500) {
    return `${filename}: Server is still having trouble. Please retry in a moment — the file was NOT charged against your batch, we detect and skip duplicates.`;
  }
  if (status === 413) {
    return `${filename}: File too large. Split the PDF or use a smaller scan resolution.`;
  }
  if (status === 415 || status === 400) {
    return `${filename}: We couldn't recognise this as a PDF or image.`;
  }
  if (err?.code === 'ECONNABORTED') {
    return `${filename}: Upload timed out. Check your internet connection and retry.`;
  }
  return `${filename}: Upload failed. Check your connection and retry.`;
};
